import React, { useCallback } from 'react'
import styled from '@emotion/styled'
import { Radio, DatePicker, Checkbox, Button } from 'antd'
import { createSimpleStore } from 'react-simple-reducer'
import dayjs from 'dayjs'
import Axios from 'axios'
import { DesempenhoCursosChart } from './DesempenhoCursosChart'
import { DesempenhoCursosTable } from './DesempenhoCursosTable'

const { RangePicker } = DatePicker

export interface BiCourseInfo {
  id: number
  nomeReduzido: string
  categorias: string
}

export interface BiCursoPoint {
  cursoId: number
  bucket: string
  count: number
  valorNewDriverSum: number
}

export type CursoMetric = 'count' | 'valorNewDriverSum'
type Tipo = 'todos' | 'venda_direta' | 'parceiros'
export type Granularity = 'daily' | 'weekly' | 'monthly'

const MOTO_CURSO_IDS = [13, 14, 16, 17]

export const AUTOFILTERS: Record<string, (courses: BiCourseInfo[]) => number[]> = {
  Todos: (courses) => courses.map((c) => c.id),
  Especializados: (courses) => courses.filter((c) => c.categorias === 'Curso de Especialização').map((c) => c.id),
  Atualizações: (courses) => courses.filter((c) => c.categorias === 'Curso de Atualização').map((c) => c.id),
  Reciclagem: (courses) => courses.filter((c) => c.categorias === 'Curso de Reciclagem').map((c) => c.id),
  Livres: (courses) => courses.filter((c) => c.categorias === 'Curso Livre').map((c) => c.id),
  Moto: (courses) => courses.filter((c) => MOTO_CURSO_IDS.includes(c.id)).map((c) => c.id),
}

export const BiDesempenhoCursosStore = createSimpleStore(
  {
    tipo: 'todos' as Tipo,
    metric: 'valorNewDriverSum' as CursoMetric,
    granularity: 'daily' as Granularity,
    dateRange: [startOfCurrentMonth(), today()] as [string, string],
    loading: false as boolean,
    courses: [] as BiCourseInfo[],
    points: [] as BiCursoPoint[],
    selectedCursoIds: [] as number[],
    error: null as string | null,
    periodStart: currentPeriodStart('daily'),
    periodPoints: [] as BiCursoPoint[],
    periodLoading: false as boolean,
  },
  {
    setTipo: (state, tipo: Tipo) => ({ ...state, tipo }),
    setMetric: (state, metric: CursoMetric) => ({ ...state, metric }),
    setGranularity: (state, granularity: Granularity) => ({ ...state, granularity }),
    setDateRange: (state, dateRange: [string, string]) => ({ ...state, dateRange }),
    setSelectedCursoIds: (state, selectedCursoIds: number[]) => ({ ...state, selectedCursoIds }),
    fetchStarted: (state) => ({ ...state, loading: true, error: null }),
    fetchSuccess: (state, payload: { courses: BiCourseInfo[]; points: BiCursoPoint[] }) => {
      const newCourseIds = payload.courses.map((c) => c.id)
      const selectedCursoIds =
        state.selectedCursoIds.length === 0
          ? newCourseIds
          : state.selectedCursoIds.filter((id) => newCourseIds.includes(id))
      return { ...state, courses: payload.courses, points: payload.points, selectedCursoIds, loading: false }
    },
    fetchFailed: (state, error: string) => ({ ...state, loading: false, error }),
    setPeriodStart: (state, periodStart: string) => ({ ...state, periodStart }),
    periodFetchStarted: (state) => ({ ...state, periodLoading: true }),
    periodFetchSuccess: (state, periodPoints: BiCursoPoint[]) => ({ ...state, periodPoints, periodLoading: false }),
    periodFetchFailed: (state) => ({ ...state, periodPoints: [], periodLoading: false }),
  },
  {
    thunks: {
      fetchData(tipo: Tipo, startDate: string, endDate: string, granularity: Granularity) {
        return async (dispatch: any) => {
          dispatch(BiDesempenhoCursosStore.actions.fetchStarted())
          try {
            const { data } = await Axios.get('/matriculas/bi/cursos', {
              params: { tipo, startDate, endDate, granularity },
            })
            dispatch(BiDesempenhoCursosStore.actions.fetchSuccess(data))
          } catch {
            dispatch(BiDesempenhoCursosStore.actions.fetchFailed('Erro ao carregar dados'))
          }
        }
      },
      fetchPeriodData(tipo: Tipo, granularity: Granularity, periodStart: string) {
        return async (dispatch: any) => {
          dispatch(BiDesempenhoCursosStore.actions.setPeriodStart(periodStart))
          dispatch(BiDesempenhoCursosStore.actions.periodFetchStarted())
          try {
            const startDate = shiftPeriod(periodStart, granularity, -1)
            const endDate = periodEnd(shiftPeriod(periodStart, granularity, 1), granularity)
            const { data } = await Axios.get('/matriculas/bi/cursos', {
              params: { tipo, startDate, endDate, granularity },
            })
            dispatch(BiDesempenhoCursosStore.actions.periodFetchSuccess(data.points))
          } catch {
            dispatch(BiDesempenhoCursosStore.actions.periodFetchFailed())
          }
        }
      },
    },
  }
)

export function BiDesempenhoCursos() {
  const init = useCallback((dispatch: any) => {
    dispatch(BiDesempenhoCursosStore.thunks.fetchData('todos', startOfCurrentMonth(), today(), 'daily'))
    dispatch(BiDesempenhoCursosStore.thunks.fetchPeriodData('todos', 'daily', currentPeriodStart('daily')))
  }, [])

  return (
    <BiDesempenhoCursosStore.Provider init={init}>
      <Controls>
        <TipoToggle />
        <DateRangePickerControl />
        <MetricaToggle />
        <GranularidadeToggle />
      </Controls>
      <AutofilterButtons />
      <CourseCheckboxList />
      <DesempenhoCursosChart />
      <DesempenhoCursosTable />
    </BiDesempenhoCursosStore.Provider>
  )
}

function TipoToggle() {
  const { tipo, dateRange, granularity, periodStart } = BiDesempenhoCursosStore.useState()
  const dispatch = BiDesempenhoCursosStore.useDispatch()

  function handleChange(newTipo: Tipo) {
    dispatch(BiDesempenhoCursosStore.actions.setTipo(newTipo))
    dispatch(BiDesempenhoCursosStore.thunks.fetchData(newTipo, dateRange[0], dateRange[1], granularity))
    dispatch(BiDesempenhoCursosStore.thunks.fetchPeriodData(newTipo, granularity, periodStart))
  }

  return (
    <Radio.Group
      value={tipo}
      onChange={(e) => handleChange(e.target.value)}
      optionType="button"
      buttonStyle="solid"
      options={[
        { label: 'Todos', value: 'todos' },
        { label: 'Venda Direta', value: 'venda_direta' },
        { label: 'Parceiros', value: 'parceiros' },
      ]}
    />
  )
}

function DateRangePickerControl() {
  const { dateRange, tipo, granularity } = BiDesempenhoCursosStore.useState()
  const dispatch = BiDesempenhoCursosStore.useDispatch()

  function handleChange(dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) {
    if (!dates || !dates[0] || !dates[1]) return
    const start = dates[0].format('YYYY-MM-DD')
    const end = dates[1].format('YYYY-MM-DD')
    dispatch(BiDesempenhoCursosStore.actions.setDateRange([start, end]))
    dispatch(BiDesempenhoCursosStore.thunks.fetchData(tipo, start, end, granularity))
  }

  return (
    <RangePicker
      value={[dayjs(dateRange[0]), dayjs(dateRange[1])]}
      onChange={handleChange}
      disabledDate={(d) => d.isAfter(dayjs(), 'day')}
      format="DD/MM/YYYY"
      allowClear={false}
    />
  )
}

function MetricaToggle() {
  const { metric } = BiDesempenhoCursosStore.useState()
  const dispatch = BiDesempenhoCursosStore.useDispatch()

  return (
    <Radio.Group
      value={metric}
      onChange={(e) => dispatch(BiDesempenhoCursosStore.actions.setMetric(e.target.value))}
      optionType="button"
      options={[
        { label: 'Receita', value: 'valorNewDriverSum' },
        { label: 'Matrículas', value: 'count' },
      ]}
    />
  )
}

function GranularidadeToggle() {
  const { granularity, tipo, dateRange } = BiDesempenhoCursosStore.useState()
  const dispatch = BiDesempenhoCursosStore.useDispatch()

  function handleChange(newGranularity: Granularity) {
    dispatch(BiDesempenhoCursosStore.actions.setGranularity(newGranularity))
    dispatch(BiDesempenhoCursosStore.thunks.fetchData(tipo, dateRange[0], dateRange[1], newGranularity))
    dispatch(BiDesempenhoCursosStore.thunks.fetchPeriodData(tipo, newGranularity, currentPeriodStart(newGranularity)))
  }

  return (
    <Radio.Group
      value={granularity}
      onChange={(e) => handleChange(e.target.value)}
      optionType="button"
      options={[
        { label: 'Diário', value: 'daily' },
        { label: 'Semanal', value: 'weekly' },
        { label: 'Mensal', value: 'monthly' },
      ]}
    />
  )
}

function AutofilterButtons() {
  const { courses } = BiDesempenhoCursosStore.useState()
  const dispatch = BiDesempenhoCursosStore.useDispatch()

  function handleAutofilter(label: string) {
    const ids = AUTOFILTERS[label](courses)
    dispatch(BiDesempenhoCursosStore.actions.setSelectedCursoIds(ids))
  }

  return (
    <AutofilterRow>
      {Object.keys(AUTOFILTERS).map((label) => (
        <Button key={label} size="small" onClick={() => handleAutofilter(label)}>
          {label}
        </Button>
      ))}
    </AutofilterRow>
  )
}

function CourseCheckboxList() {
  const { courses, selectedCursoIds } = BiDesempenhoCursosStore.useState()
  const dispatch = BiDesempenhoCursosStore.useDispatch()

  function handleToggle(cursoId: number, checked: boolean) {
    const next = checked
      ? [...selectedCursoIds, cursoId]
      : selectedCursoIds.filter((id) => id !== cursoId)
    dispatch(BiDesempenhoCursosStore.actions.setSelectedCursoIds(next))
  }

  return (
    <CheckboxRow>
      {courses.map((course) => (
        <Checkbox
          key={course.id}
          checked={selectedCursoIds.includes(course.id)}
          onChange={(e) => handleToggle(course.id, e.target.checked)}
        >
          {course.nomeReduzido}
        </Checkbox>
      ))}
    </CheckboxRow>
  )
}

const Controls = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 8px;
`

const AutofilterRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`

const CheckboxRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`

function startOfCurrentMonth(): string {
  return dayjs().startOf('month').format('YYYY-MM-DD')
}

function today(): string {
  return new Date().toLocaleDateString('en-CA')
}

export function currentPeriodStart(granularity: Granularity): string {
  return periodStartOf(dayjs(), granularity)
}

export function shiftPeriod(periodStart: string, granularity: Granularity, steps: number): string {
  const unit = granularity === 'daily' ? 'day' : granularity === 'weekly' ? 'week' : 'month'
  return periodStartOf(dayjs(periodStart).add(steps, unit), granularity)
}

export function periodEnd(periodStart: string, granularity: Granularity): string {
  const start = dayjs(periodStart)
  if (granularity === 'daily') return periodStart
  if (granularity === 'weekly') return start.add(6, 'day').format('YYYY-MM-DD')
  return start.endOf('month').format('YYYY-MM-DD')
}

export function periodBucket(periodStart: string, granularity: Granularity): string {
  return granularity === 'monthly' ? periodStart.slice(0, 7) : periodStart
}

function periodStartOf(date: dayjs.Dayjs, granularity: Granularity): string {
  if (granularity === 'daily') return date.format('YYYY-MM-DD')
  if (granularity === 'weekly') return date.subtract(date.day(), 'day').format('YYYY-MM-DD')
  return date.startOf('month').format('YYYY-MM-DD')
}
